import { useState, useMemo } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Ban, CheckCircle2, Loader2, Search, ExternalLink,
  ClipboardList, Hammer, Check, CheckCheck,
} from "lucide-react";
import { FENCE_LABELS, fmtDate, fmtCurrencyExact } from "@/lib/constants";

// Install Jobs — list-only view. All calendar / date-block scheduling lives
// on the single Scheduler page (`/scheduler`), which pulls jobs via the
// unified /api/scheduler/events feed. This page is deliberately focussed on
// "what jobs are on the go, and how are they tracking?" — not "who's on-site
// on Tuesday?" (that's the Scheduler's job).

const STATUS_COLOR = {
  awaiting_deposit: "border-amber-300 bg-amber-50 text-amber-800",
  scheduled: "border-sky-300 bg-sky-50 text-sky-800",
  in_progress: "border-cyan-300 bg-cyan-50 text-cyan-700",
  on_hold: "border-amber-300 bg-amber-50 text-amber-800",
  completed: "border-emerald-300 bg-emerald-50 text-emerald-800",
  handover: "border-violet-300 bg-violet-50 text-violet-800",
  cancelled: "border-rose-300 bg-rose-50 text-rose-700",
};

const CLOSED_STATUSES = ["completed", "cancelled"];

export default function Jobs() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: unreadData } = useQuery({
    queryKey: ["jobs-unread"],
    queryFn: async () => (await api.get("/jobs/unread/summary")).data,
    refetchInterval: 60000,
  });
  const unreadMap = unreadData?.jobs || {};
  const unreadTotal = unreadData?.total || 0;
  const markRead = useMutation({
    mutationFn: async (id) => api.post(`/jobs/${id}/mark-read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs-unread"] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => api.post("/jobs/unread/mark-all"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs-unread"] }); toast.success("All job updates marked as read"); },
  });
  const [openClosed, setOpenClosed] = useState("open");
  const [search, setSearch] = useState("");
  const [cancelJob, setCancelJob] = useState(null);
  const [completeJob, setCompleteJob] = useState(null);

  const { data: allJobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });
  const { data: companies = [] } = useQuery({ queryKey: ["companies-all-j"], queryFn: async () => (await api.get("/companies")).data });
  const coMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const jobs = useMemo(() => {
    let list = allJobs.filter((j) =>
      openClosed === "all" ? true :
      openClosed === "closed" ? CLOSED_STATUSES.includes(j.status) :
      !CLOSED_STATUSES.includes(j.status));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((j) =>
        (j.title || "").toLowerCase().includes(q)
        || (j.job_number || "").toLowerCase().includes(q)
        || (j.site_address || "").toLowerCase().includes(q)
        || (coMap[j.company_id] || "").toLowerCase().includes(q));
    }
    return list;
  }, [allJobs, openClosed, search, coMap]);

  const cancel = useMutation({
    mutationFn: async (jid) => (await api.post(`/jobs/${jid}/cancel`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["scheduler-events"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      setCancelJob(null);
      toast.success("Job cancelled — bookings cleared and linked deal moved to Lost");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not cancel job"),
  });

  const complete = useMutation({
    mutationFn: async (jid) => (await api.patch(`/jobs/${jid}`, { status: "completed" })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["scheduler-events"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      setCompleteJob(null);
      toast.success("Job marked as completed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not complete job"),
  });

  const progressOf = (j) => {
    // 5-step install workflow pct (same as the job page), computed server-side.
    if (typeof j.workflow_pct === "number") return j.workflow_pct;
    if (typeof j.progress_pct === "number") return j.progress_pct;
    const ms = j.milestones || [];
    return ms.length ? (ms.filter((m) => m.completed).length / ms.length) * 100 : 0;
  };

  return (
    <div>
      <PageHeader
        title="Install Jobs"
        subtitle="Every install job in the pipeline — scheduled dates flow through to the Scheduler calendar."
        breadcrumb={["Operations", "Install Jobs"]}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => nav("/scheduler")}
              className="rounded-sm h-10"
              data-testid="jump-to-scheduler-btn"
            >
              <ClipboardList size={14} className="mr-1.5" /> Open Scheduler
            </Button>
            {/* No "New install job" button — installs are created automatically
                when a deal is marked Won. See workflow.auto_job_on_deal_won.
                See iter-61 for the rationale (single JN- number continuity). */}
          </>
        }
      />

      <div className="px-6 lg:px-10 py-6">
        <Card className="rounded-sm border-slate-200 industrial-shadow">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="stamp-label text-slate-600 shrink-0">Install jobs ({jobs.length}{openClosed !== "all" ? ` ${openClosed}` : ""})</div>
            <div className="relative flex-1 sm:max-w-xs">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="jobs-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, job #, client or address..."
                className="pl-8 h-9 rounded-sm text-sm"
              />
            </div>
            <Select value={openClosed} onValueChange={setOpenClosed}>
              <SelectTrigger className="h-9 w-full sm:w-44 rounded-sm text-xs" data-testid="jobs-open-closed-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open jobs</SelectItem>
                <SelectItem value="closed">Closed (done/cancelled)</SelectItem>
                <SelectItem value="all">All jobs</SelectItem>
              </SelectContent>
            </Select>
            <DefaultViewControl screen="jobs"
              current={{ open_closed: openClosed }}
              onApply={(f) => { if (f.open_closed) setOpenClosed(f.open_closed); }} />
            {unreadTotal > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <span data-testid="jobs-unread-total" className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 whitespace-nowrap">
                  {unreadTotal} unread update{unreadTotal === 1 ? "" : "s"}
                </span>
                <Button size="sm" variant="outline" data-testid="jobs-mark-all-read" onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending} className="h-9 rounded-sm text-xs whitespace-nowrap">
                  <CheckCheck size={14} className="mr-1.5" /> Mark all read
                </Button>
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-3">Job #</th>
                  <th className="px-4 py-3">Client / title</th>
                  <th className="px-4 py-3">Site address</th>
                  <th className="px-4 py-3">Fence type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 w-40">Progress</th>
                  <th className="px-4 py-3">Scheduled</th>
                  <th className="px-4 py-3 text-right">Sale price</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((j) => {
                  const progress = progressOf(j);
                  return (
                    <tr
                      key={j.id}
                      className={`hover:bg-cyan-50/30 cursor-pointer ${unreadMap[j.id] ? "bg-sky-50/60" : ""}`}
                      onClick={() => nav(`/jobs/${j.id}`)}
                      data-testid={`job-row-${j.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-emerald-100 text-emerald-700 flex items-center justify-center rounded-sm shrink-0">
                            <Hammer size={13} />
                          </div>
                          <div className="font-mono text-xs font-semibold text-slate-900 inline-flex items-center gap-1.5">
                            {unreadMap[j.id] && <span className="w-2 h-2 rounded-full bg-sky-500" data-testid={`job-unread-dot-${j.id}`} />}
                            {j.job_number || "—"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[280px]" title={j.title}>
                          {j.title}
                          {unreadMap[j.id] && <Badge data-testid={`job-unread-pill-${j.id}`} className="ml-2 rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{unreadMap[j.id]} new</Badge>}
                        </div>
                        {coMap[j.company_id] && <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{coMap[j.company_id]}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs truncate max-w-[220px]" title={j.site_address}>{j.site_address || "—"}</td>
                      <td className="px-4 py-3 text-xs">
                        <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300">
                          {FENCE_LABELS[j.fence_type] || j.fence_type || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_COLOR[j.status] || ""}`}>{(j.status || "").replace("_", " ")}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[10px] text-slate-500 stamp-label mb-1">{Math.round(progress)}%</div>
                        <div className="w-full h-1.5 bg-slate-100 overflow-hidden">
                          <div className="h-full bg-cyan-500" style={{ width: `${progress}%` }} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {j.scheduled_start ? fmtDate(j.scheduled_start) : <span className="text-slate-400">Not booked</span>}
                        {j.scheduled_end && j.scheduled_end !== j.scheduled_start && (
                          <div className="text-[10px] text-slate-400">→ {fmtDate(j.scheduled_end)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 text-xs">
                        {typeof j.sale_price === "number" && j.sale_price > 0 ? fmtCurrencyExact(j.sale_price) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          {unreadMap[j.id] && (
                            <button
                              type="button"
                              title="Mark as read"
                              data-testid={`job-mark-read-${j.id}`}
                              onClick={() => markRead.mutate(j.id)}
                              className="text-emerald-700 hover:text-emerald-800 p-1 inline-flex items-center text-[10px] font-semibold"
                            >
                              <Check size={13} className="mr-0.5" />Read
                            </button>
                          )}
                          <button
                            type="button"
                            title="Open job"
                            data-testid={`progress-job-${j.id}`}
                            onClick={() => nav(`/jobs/${j.id}`)}
                            className="text-slate-400 hover:text-cyan-600 p-1"
                          >
                            <ClipboardList size={13} />
                          </button>
                          {!CLOSED_STATUSES.includes(j.status) && (
                            <button
                              type="button"
                              title="Mark job completed"
                              data-testid={`complete-job-${j.id}`}
                              onClick={() => setCompleteJob(j)}
                              className="text-slate-400 hover:text-emerald-600 p-1"
                            >
                              <CheckCircle2 size={13} />
                            </button>
                          )}
                          {!CLOSED_STATUSES.includes(j.status) && (
                            <button
                              type="button"
                              title="Cancel job"
                              data-testid={`cancel-job-${j.id}`}
                              onClick={() => setCancelJob(j)}
                              className="text-slate-400 hover:text-rose-600 p-1"
                            >
                              <Ban size={13} />
                            </button>
                          )}
                          <button
                            type="button"
                            title="Open job detail"
                            data-testid={`view-job-${j.id}`}
                            onClick={() => nav(`/jobs/${j.id}`)}
                            className="text-slate-400 hover:text-slate-700 p-1"
                          >
                            <ExternalLink size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {jobs.length === 0 && <tr><td colSpan={9} className="py-12 text-center text-slate-400 text-sm">
                  <div className="text-slate-500 font-medium mb-1">No jobs match this filter</div>
                  <div className="text-[11px]">Install jobs are auto-created when a deal is marked Won — go to <button type="button" className="text-cyan-600 underline" onClick={() => nav("/deals")}>Deals</button> to progress an enquiry.</div>
                </td></tr>}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-slate-100">
            {jobs.map((j) => {
              const progress = progressOf(j);
              return (
                <div key={j.id} className={`px-4 py-3 ${unreadMap[j.id] ? "bg-sky-50/70" : ""}`} onClick={() => nav(`/jobs/${j.id}`)} data-testid={`job-card-${j.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-emerald-100 text-emerald-700 flex items-center justify-center rounded-sm shrink-0">
                      <Hammer size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 text-sm truncate">
                            {j.title}
                            {unreadMap[j.id] && <Badge className="ml-1.5 rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400">{unreadMap[j.id]} new</Badge>}
                          </div>
                          <div className="font-mono text-[10px] text-slate-400 truncate">{j.job_number}{coMap[j.company_id] ? ` · ${coMap[j.company_id]}` : ""}</div>
                        </div>
                        <Badge variant="outline" className={`rounded-sm text-[9px] shrink-0 ${STATUS_COLOR[j.status] || ""}`}>{(j.status || "").replace("_", " ")}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 truncate">{j.site_address || "No address"}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 h-1.5 bg-slate-100 overflow-hidden rounded-sm">
                          <div className="h-full bg-cyan-500" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-slate-500 shrink-0">{Math.round(progress)}%</span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="text-[10px] text-slate-500">
                          {j.scheduled_start ? fmtDate(j.scheduled_start) : "Not booked"}
                        </div>
                        <div className="font-mono text-[11px] font-semibold text-slate-900">
                          {typeof j.sale_price === "number" && j.sale_price > 0 ? fmtCurrencyExact(j.sale_price) : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {jobs.length === 0 && <div className="py-12 text-center text-slate-400 text-sm px-6">
              <div className="text-slate-500 font-medium mb-1">No jobs match this filter</div>
              <div className="text-[11px]">Install jobs are auto-created when a deal is marked Won — go to <button type="button" className="text-cyan-600 underline" onClick={() => nav("/deals")}>Deals</button> to progress an enquiry.</div>
            </div>}
          </div>
        </Card>
      </div>

      {/* Cancel job confirm */}
      <Dialog open={!!cancelJob} onOpenChange={(o) => !o && setCancelJob(null)}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Cancel this job?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            <b>{cancelJob?.title}</b> ({cancelJob?.job_number || "no JN"}) will be marked <b>Cancelled</b>.
            All installer bookings are removed, it's excluded from profit reporting, and the linked deal moves to <b>Lost</b>.
            The job stays visible under Closed jobs.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelJob(null)} className="rounded-sm">Keep job</Button>
            <Button data-testid="confirm-cancel-job-btn" onClick={() => cancel.mutate(cancelJob.id)} disabled={cancel.isPending}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {cancel.isPending ? "Cancelling…" : "Cancel job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete job confirm */}
      <Dialog open={!!completeJob} onOpenChange={(o) => !o && setCompleteJob(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="complete-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-500" /> Mark this job as completed?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <b>{completeJob?.title}</b> ({completeJob?.job_number || "no JN"}) will move to <b>Completed</b> and drop
            out of the open list. Any linked deal &amp; downstream automations for <code>job_completed</code> will fire.
            You can re-open the job from its detail page if needed.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteJob(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-complete-job-btn" onClick={() => complete.mutate(completeJob.id)} disabled={complete.isPending}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              {complete.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Mark completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
